import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  updatePlayer,
  UpdatePlayerContext,
  useApi,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { Form, FormApi } from 'informed';
import { useCurrentRoute, useNavigation } from 'react-navi';

import { register, RegistrationFormApi } from 'api/auth';
import { Button } from 'components/buttons';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Password } from 'components/form/Password';
import { ModalDefaultProps } from 'components/Modal';
import { emailRe, validatePassword } from 'helpers/validators';
import { ModalTemplate } from 'components/ModalTemplate';
import { Logo } from 'components/Logo';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { mergeRefs, removeQueryParameterWithNavigation } from 'helpers/common';
import { AUTO_REGISTRATION_QUERY_NAME } from 'helpers/auth';
import { showTooltip } from 'helpers/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';
import { RegistrationBanner } from '../Registration/components/RegistrationBanner';
import { obscureEmail } from 'helpers/masks';
import { AddonsContext } from 'contexts/addons';

import { ScFormSubmitGroup } from 'components/form/groups/styled';
import { ScEmailText } from './styled';

import background from 'assets/images/backgrounds/modals/registration.jpg';

type AutoRegistrationFormFields = Omit<
  RegistrationFormApi,
  'email' | 'currency'
>;

export const AutoRegistration = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const { t } = useTranslation('glossary');
    const {
      url: { query },
    } = useCurrentRoute();
    const navigation = useNavigation();

    const containerRef = useRef<HTMLDivElement>(null);

    const { isMobileS } = useContext(CommonUtilsContext);

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? {
            type: 'mobile',
            mode: {
              type: 'background',
              src: background,
            },
          }
        : {
            type: 'desktop',
            size: 'small',
            mode: {
              type: 'background',
              src: background,
            },
          };
    }, [isMobileS]);

    const handleModalClose = useCallback(() => {
      removeQueryParameterWithNavigation(
        navigation,
        query,
        AUTO_REGISTRATION_QUERY_NAME
      );

      onClose && onClose();
    }, [onClose, navigation, query]);

    return (
      <>
        <ModalTemplate
          ref={mergeRefs(ref, containerRef)}
          onClose={handleModalClose}
          settings={modalSettings}
          title={<Logo height={28} />}
          description={t('autoRegText')}
          header={<RegistrationBanner />}
        >
          <AutoRegistrationForm onClose={handleModalClose} />
        </ModalTemplate>
      </>
    );
  }
);

const AutoRegistrationForm: FC<ModalDefaultProps> = ({ onClose }) => {
  const { t } = useTranslation(['glossary', 'common']);

  const [
    requestRegister,
    { error: registerError, pending: registerPending },
  ] = useApi(register);
  const [
    requestUpdatePlayer,
    { pending: pendingUpdatePlayer, error: updatePlayerErrors },
  ] = useApi(updatePlayer);

  const { pendingUser, fetchUser } = useContext(AuthContext);
  const { autoRegistrationData } = useContext(AddonsContext);

  const formApiRef = useRef<FormApi<RegistrationFormApi>>();

  const error = useMemo(() => {
    if (registerError == null || registerError.errors == null) return null;

    if (
      registerError.errors.email != null &&
      registerError.errors.email.taken != null
    ) {
      return registerError.errors.email.taken;
    }

    if (registerError.errors != null || updatePlayerErrors?.errors != null) {
      const firstError = Object.keys([
        ...registerError.errors,
        ...updatePlayerErrors?.errors,
      ])
        .map((key) => Object.values(registerError.errors[key]))
        .flat();

      return firstError;
    }
  }, [registerError, updatePlayerErrors?.errors]);

  const handleSubmit = useCallback(
    async ({ password }: AutoRegistrationFormFields) => {
      if (!autoRegistrationData?.email || !autoRegistrationData.currency)
        return;

      if (!emailRe.test(autoRegistrationData.email)) {
        showTooltip({
          type: TooltipTypes.ERROR,
          title: 'Email is wrong',
        });
        return;
      }
      const { email, currency, ...additionalInfo } = autoRegistrationData;
      const { error, response } = await requestRegister({
        email,
        currency,
        password,
        ageAcceptance: true,
        termsAcceptance: true,
        receivePromos: true,
        receivePromosViaPhoneCalls: true,
        receiveSmsPromos: true,
        agreedToPartnerPromotions: true,
      }).request;

      if (error) {
        showTooltip({
          type: TooltipTypes.ERROR,
          title: `${t('common:auth.login.error.unknown')}`,
        });
        return;
      }

      if (response?.data.id) {
        const { error: updatePlayerError } = await requestUpdatePlayer({
          context: UpdatePlayerContext.Edition,
          player: additionalInfo,
        }).request;

        if (updatePlayerError) {
          showTooltip({
            type: TooltipTypes.ERROR,
            title: `${t('common:auth.login.error.unknown')}`,
          });
          return;
        }
      }
      await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

      onClose && onClose();
    },
    [
      fetchUser,
      requestRegister,
      requestUpdatePlayer,
      autoRegistrationData,
      onClose,
      t,
    ]
  );

  return (
    <Form<RegistrationFormApi>
      getApi={(api) => {
        formApiRef.current = api;
      }}
      onSubmit={handleSubmit}
    >
      <ScEmailText>
        <p>{obscureEmail(autoRegistrationData?.email)}</p>
      </ScEmailText>

      <FormGroup error={error}>
        <Password
          animatedPlaceholder={'password'}
          field="password"
          validate={validatePassword}
          validateOnChange
          validateOnBlur
          strengthChecker
        />
      </FormGroup>

      <ScFormSubmitGroup>
        <Button
          green
          xLarge
          wide
          pending={registerPending || pendingUser || pendingUpdatePlayer}
          type="submit"
        >
          {t('joinNow')}
        </Button>
      </ScFormSubmitGroup>
    </Form>
  );
};
